import { SearchOutlined } from "@ant-design/icons";
import { Divider, Empty, Input, Radio } from "antd";
import { debounce, throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useEventBus from '@/hooks/useEventBus';

import { globalSearchAPI } from '@/services/system';
import CardItem from "./CardItem";
import { PUSH_SINGLE_ROUTER } from "@/constants/event-bus";
import QdLoading from "@/components/Common/QdLoading";

interface SearchResultItem {
    data: any[];
    total: number;
}

interface SearchComponentProps {
    email_ticket: SearchResultItem,
    saas_ticket: SearchResultItem
}

enum SearchType {
    ALL = 'all',
    EMAIL = 'email',
    SAAS = 'saas'
}

const SearchComponent = () => {
    const cancelRequestRef = useRef<{ cancel: () => void }>(); // 用于取消请求
    const { emit } = useEventBus();
    const [keyWord, setKeyWord] = useState<string>('');
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchResult, setSearchResult] = useState<SearchComponentProps>({
        email_ticket: { data: [], total: 0 },
        saas_ticket: { data: [], total: 0 }
    });
    const [activeKey, setActiveKey] = useState<SearchType>(SearchType.ALL);
    const inputRef = useRef<any>(null);

    // 处理点击外部关闭弹出框
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            // 如果点击的不是按钮也不是弹出框内部
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowSearch(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        // 清理函数
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // useEffect(() => {
    //     if (id) {
    //         setKeyWord(id);
    //         sessionStorage.setItem(
    //             'global-search-key',
    //             id,
    //         );
    //         // handleSearch({ target: { value: id } });
    //     } else {
    //         const key = sessionStorage.getItem('global-search-key');
    //         if (key) {
    //             setKeyWord(key);
    //             // handleSearch({ target: { value: key } });
    //         }
    //     }
    // }, []);

    const options = [
        { label: '所有', value: SearchType.ALL },
        { label: '邮件工单', value: SearchType.EMAIL },
        // { label: '在线工单', value: SearchType.SAAS },
    ];

    // 全局搜索
    const handleSearch = useCallback(debounce(async (event: { target: { value: string; }; }) => {
        const value = event.target.value || '';
        if (value.trim().length > 1) {
            setSearchLoading(true);
            setShowSearch(true);
            try {
                cancelRequestRef.current?.cancel?.(); // 取消上一个请求
                const { run, cancel } = globalSearchAPI({ content: value.trim() });
                cancelRequestRef.current = { cancel };
                const { email_ticket, saas_ticket } = await run();
                const _email_ticket = {
                    ...email_ticket,
                    data: email_ticket.data.map((item: any) => {
                        const { subject, ticket_number, ..._item } = item;
                        return {
                            ..._item,
                            title: subject,
                            ticket_number: `#${ticket_number}`,
                        }
                    })
                };
                const _saas_ticket = {
                    ...saas_ticket,
                    data: saas_ticket.data.map((item: any) => {
                        const { reason_name, ..._item } = item;
                        return {
                            ..._item,
                            title: reason_name,
                        }
                    })
                };
                setSearchResult({
                    email_ticket: _email_ticket,
                    saas_ticket: _saas_ticket
                });
            } catch (error) {
                // 如果捕获到取消请求抛出的异常则不进行任何操作
                if (error?.code === "ERR_CANCELED") {
                    return;
                }

                throw error;
            }
            finally {
                setSearchLoading(false);
            }
        }
    }, 300), []);

    const userInput = (event: { target: { value: string; }; }) => {
        const value = event.target.value || '';
        setKeyWord(value);
        handleSearch(event);
    }

    // 聚焦时显示搜索框
    const focusInput = useCallback((event: any) => {
        // const value = event.target.value || '';
        // if (value.length > 1) {
        setShowSearch(true);
        // }
        // // 搜索结果为空时，重新搜索
        // if (value.trim().length > 1 && searchResult.email_ticket.data.length === 0 && searchResult.saas_ticket.data.length === 0) {
        //     handleSearch({ target: { value } });
        // }
    }, [searchResult]);

    // 限制搜索数量
    const limtNumber = useMemo(() => {
        return activeKey === SearchType.ALL ? 3 : 10;
    }, [activeKey]);

    // 渲染列表
    const renderList = useCallback((type: 'email' | 'saas') => {
        let ticketList = type === 'email' ? searchResult.email_ticket.data : searchResult.saas_ticket.data;
        if (!ticketList || !ticketList.length) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }
        return (ticketList.slice(0, limtNumber) || [])?.map((item) => <div key={`${type}_${item.id}`} ><CardItem callback={() => { setShowSearch(false); setKeyWord(''); }} type={type} searchResultItem={item} /></div>);
    }, [searchResult, limtNumber]);

    // 跳转搜索详情
    const linkToSearch = useCallback(throttle((type: 'email' | 'saas') => {
        const routeMap = {
            email: '/email-ticket/search/',
            saas: '/saas-ticket/search/'
        };
        setKeyWord('');
        setShowSearch(false);
        emit(PUSH_SINGLE_ROUTER, { path: `${routeMap[type]}${encodeURIComponent(keyWord.trim()).replaceAll('*', '%2A')}`, router: [`${routeMap[type]}:id`] });
    }, 200), [keyWord]);

    const isEmptyMap = useMemo(() => {
        const emailTotal = searchResult?.email_ticket?.total ?? 0;
        const saasTotal = searchResult?.saas_ticket?.total ?? 0;
        return {
            [SearchType.ALL]: emailTotal === 0 && saasTotal === 0,
            [SearchType.EMAIL]: emailTotal === 0,
            [SearchType.SAAS]: saasTotal === 0,
        };
    }, [searchResult?.email_ticket?.total, searchResult?.saas_ticket?.total]);


    return (
        <div ref={inputRef} className="qd-relative !qd-bg-transparent !qd-cursor-auto !qd-p-0 qd-w-80" >
            <Input value={keyWord} onChange={userInput} prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} size="middle" placeholder="搜索" allowClear onFocus={focusInput} />
            {showSearch && (
                <div className="!qd-leading-normal qd-absolute qd-left-0 qd-top-full qd-w-full qd-rounded-lg qd-bg-white qd-shadow-lg qd-border qd-border-gray-200 qd-p-4">
                    <Radio.Group
                        value={activeKey}
                        block
                        options={options}
                        onChange={(e) => setActiveKey(e.target.value)}
                        defaultValue="all"
                        optionType="button"
                        buttonStyle="solid"
                    />
                    {keyWord.trim().length > 1 && <>
                        <QdLoading loading={searchLoading} size={32}/>
                        {((activeKey === SearchType.ALL || activeKey === SearchType.EMAIL) && searchResult?.email_ticket?.total > 0) && <div className="qd-pb-2 qd-pt-4">
                            <div className="qd-grid qd-grid-cols-[1fr_auto] qd-pb-2" >
                                <span className="qd-text-black qd-font-medium" >邮件工单</span>
                                {searchResult?.email_ticket.total > limtNumber && <a onClick={() => linkToSearch('email')} className="qd-text-title-blue qd-font-normal qd-text-sm" >查看全部({searchResult.email_ticket.total})</a>}
                            </div>
                            <div className="qd-px-2" >
                                {renderList('email')}
                            </div>
                        </div>}
                        {activeKey === SearchType.ALL && searchResult?.email_ticket?.total > 0 && searchResult?.saas_ticket?.total > 0 && <Divider className="qd-m-0" />}
                        {/* {((activeKey === SearchType.ALL || activeKey === SearchType.SAAS) && searchResult?.saas_ticket?.total > 0) && <div className="qd-pb-2 qd-pt-4">
                            <div className="qd-grid qd-grid-cols-[1fr_auto] qd-pb-2" >
                                <span className="qd-text-black qd-font-medium" >在线工单</span>
                                {searchResult?.saas_ticket.total > limtNumber && <a onClick={() => linkToSearch('saas')} className="qd-text-title-blue qd-font-normal qd-text-sm" >查看全部({searchResult.saas_ticket.total})</a>}
                            </div>
                            <div className="qd-px-2" >
                                {renderList('saas')}
                            </div>
                        </div>} */}
                        {isEmptyMap[activeKey] && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                    </>}
                </div>
            )}
        </div>
    );
}

export default SearchComponent;
